/* eslint-disable react/jsx-props-no-spreading -- every day column takes the grid props */
import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { CalendarDayContent, EMPTY_DAY } from './calendarDayModel';
import type { CalendarGridProps } from './calendarGridTypes';
import { ReleaseBand } from './ReleaseBand';
import { RolloutStepMark } from './RolloutStepMark';
import { WeekColumn } from './WeekColumn';
import { Panel } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The default hours of the Week view (owner answer: 09:00 to 21:00, with "Show the full day"). */
export const WEEK_FIRST_HOUR = 9;
export const WEEK_LAST_HOUR = 21;

/** The hours of the Week view: 09 to 21, wider when a post is earlier or later, or the full day. */
export function weekHours(contents: CalendarDayContent[], isFullDay: boolean): number[] {
  if (isFullDay) {
    return Array.from({ length: 24 }, (_unused, hour) => hour);
  }
  const postHours = contents.flatMap((content) => content.posts.map((post) => new Date(post.scheduledAt).getHours()));
  const first = Math.min(WEEK_FIRST_HOUR, ...postHours);
  const last = Math.max(WEEK_LAST_HOUR, ...postHours);
  return Array.from({ length: last - first + 1 }, (_unused, index) => first + index);
}

/** The Week view (design a2): the all-day lane for the release bands and the tasks, then the hours. */
const WeekGridComponent: React.FC<CalendarGridProps> = ({ days, contentByDay, ...grid }) => {
  const intl = useIntl();
  const [isFullDay, setFullDay] = useState(false);
  const [showFreeSlots, setShowFreeSlots] = useState(true);
  const contents = useMemo(() => days.map((day) => contentByDay.get(day.key) ?? EMPTY_DAY), [contentByDay, days]);
  const hours = useMemo(() => weekHours(contents, isFullDay), [contents, isFullDay]);
  const toggleFullDay = useCallback(() => setFullDay((current) => !current), []);
  const toggleSlots = useCallback(() => setShowFreeSlots((current) => !current), []);

  return (
    <Panel className={styles.cal}>
      <div className={styles.weekTools}>
        <button type="button" className={styles.toggle} aria-pressed={showFreeSlots} onClick={toggleSlots}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.week.freeSlots', defaultMessage: 'Free slots' })}
        </button>
        <button type="button" className={styles.toggle} aria-pressed={isFullDay} onClick={toggleFullDay}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.week.fullDay', defaultMessage: 'Show the full day' })}
        </button>
      </div>
      <div className={styles.wgrid} data-testid="calendar-week-grid">
        <div className={styles.whead} />
        {days.map((day, index) => (
          <div key={day.key} className={clsx(styles.whead, day.isToday && styles.wheadToday)}>
            <button type="button" className={styles.wheadDay} onClick={() => grid.onOpenDay(day.key)}>
              {intl.formatDate(day.start, { weekday: 'short', day: 'numeric' })}
            </button>
            <div className={styles.wlane}>
              {(contents[index] ?? EMPTY_DAY).releases.map((song) => (
                <ReleaseBand key={song.catalogItemUuid} song={song} />
              ))}
              {(contents[index] ?? EMPTY_DAY).steps.map((step) => (
                <RolloutStepMark key={step.rolloutKey} step={step} song={grid.songs.get(step.catalogItemUuid)} />
              ))}
            </div>
          </div>
        ))}
        <div className={styles.whours} aria-hidden="true">
          {hours.map((hour) => (
            <span key={hour} className={styles.whourLabel}>{`${String(hour).padStart(2, '0')}:00`}</span>
          ))}
        </div>
        {days.map((day, index) => (
          <WeekColumn
            key={day.key}
            day={day}
            content={contents[index] ?? EMPTY_DAY}
            hours={hours}
            showFreeSlots={showFreeSlots}
            {...grid}
          />
        ))}
      </div>
    </Panel>
  );
};

export const WeekGrid = TypedMemo(WeekGridComponent);
